import React, { forwardRef, useImperativeHandle, useRef } from "react";
import Radio, { RadioProps } from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from "@mui/material/styles";

const colors = {
  primaryBorder: "var(--primary-text)",
  hoverBorder: "var(--gradient-color)",
  hoverBackground: "var(--button-light)",
  disabledBackground: "var(--button-light)",
  disabledInnerDot: "var(--text-light)",
  gradientStart: "var(--gradient-color)",
  gradientEnd: "var(--primary-gradient-color2)",
  gradient: "linear-gradient(100.44deg, var(--gradient-color) 0%, var(--primary-gradient-color2) 110.4%)",
  surfaceWhite: "var(--surface-white)",
};

interface RadioButtonProps extends Omit<RadioProps, "onChange"> {
  label?: string;
  enabled?: boolean;
  change?: (args: any, checked: boolean) => void;
  onChange?: (args: any, checked: boolean) => void;
}

interface RadioButtonRef {
  focus: () => void;
  blur: () => void;
  element: HTMLButtonElement | null;
  current: HTMLButtonElement | null;
  checked: boolean | undefined;
}

const transitionDuration = "150ms";
const iconSize = 20; // SVG viewBox/coordinate units (matches viewBox="0 0 20 20")
const paddingSize = 6;
const innerCirleRadius = 4;
const outerCircleRadius = 8;
const iconSizeRem = `${iconSize / 16}rem`;
const paddingSizeRem = `${paddingSize / 16}rem`;
const rootSizeRem = `${(iconSize + paddingSize * 2) / 16}rem`;

const getCommonStyles = (circleType: String) => ({
  cx: 10,
  cy: 10,
  transition: `stroke ${transitionDuration} ease-in-out, fill ${transitionDuration} ease-in-out`,
  r: circleType == "inner" ? innerCirleRadius : outerCircleRadius,
  stroke: circleType == "outer" ? colors.primaryBorder : "none",
  fill: circleType == "inner" ? "url(#radio-gradient)" : colors.surfaceWhite,
});

const BaseIconSvg = styled("svg")({
  width: iconSizeRem,
  height: iconSizeRem,
  display: "block",
});

const UncheckedIconSvg = styled(BaseIconSvg)(() => ({
  "& .outerCircle": {
    ...getCommonStyles("outer"),
    strokeWidth: 1.5,
  },
}));

const CheckedIconSvg = styled(BaseIconSvg)(() => ({
  "& .outerCircle": {
    ...getCommonStyles("outer"),
    strokeWidth: 1.5,
  },
  "& .innerCircle": {
    ...getCommonStyles("inner"),
  },
}));

const CustomUncheckedIcon = (
  <UncheckedIconSvg className="unchecked-icon" viewBox="0 0 20 20">
    <circle className="outerCircle" />
  </UncheckedIconSvg>
);

const CustomCheckedIcon = (
  <CheckedIconSvg className="checked-icon" viewBox="0 0 20 20">
    <defs>
      <linearGradient id="radio-gradient" x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform="rotate(100.44)">
        <stop offset="0%" stopColor={colors.gradientStart} />
        <stop offset="110.4%" stopColor={colors.gradientEnd} />
      </linearGradient>
    </defs>
    <circle className="outerCircle" />
    <circle className="innerCircle" />
  </CheckedIconSvg>
);

const StyledRadio = styled(Radio)<Omit<RadioButtonProps, "onChange">>(() => ({
  padding: paddingSizeRem,
  margin: 0,
  color: colors.primaryBorder,
  borderRadius: "50%",
  transition: `background-color ${transitionDuration} ease-in-out`,
  width: rootSizeRem,
  height: rootSizeRem,
  boxSizing: "border-box",
  "&.Mui-disabled": {
    pointerEvents: "none",
    cursor: "default",
    backgroundColor: "transparent",
    "& .outerCircle": {
      stroke: "none",
      fill: colors.disabledBackground,
    },
    "&.Mui-checked .checked-icon .innerCircle": {
      fill: colors.disabledInnerDot,
      opacity: 1,
    },
    "& .unchecked-icon .innerHoverCircle": {
      opacity: 0,
    },
  },
}));

const RadioButton = forwardRef<RadioButtonRef, RadioButtonProps>((props, ref) => {
  const internalRef = useRef<HTMLButtonElement>(null);
  const { onChange, change, enabled, disabled: disabledProp, label, ...restProps } = props;

  const disabled = enabled !== undefined ? !enabled : disabledProp;

  useImperativeHandle(ref, () => ({
    focus: () => internalRef.current?.focus(),
    blur: () => internalRef.current?.blur(),

    get element() {
      return internalRef.current;
    },
    get current() {
      return internalRef.current;
    },

    get checked() {
      return props.checked;
    },
  }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const syncfusionArgs: any = Object.create(e, {
      event: { value: e, enumerable: true },
      checked: { value: checked, enumerable: true },
      value: { value: e.target?.value ?? "", enumerable: true },
      name: { value: e.target?.name, enumerable: true },
      target: { value: e.target, enumerable: true },
      currentTarget: { value: e.currentTarget, enumerable: true },
    });

    if (onChange) {
      onChange(syncfusionArgs, checked);
    }

    if (change) {
      change(syncfusionArgs, checked);
    }
  };

  const radioElement = (
    <StyledRadio
      icon={CustomUncheckedIcon}
      checkedIcon={CustomCheckedIcon}
      {...restProps}
      disabled={disabled}
      onChange={handleChange}
      sx={{
        opacity: `${disabled ? "50%" : "100%"}`,
      }}
      ref={internalRef}
    />
  );

  if (label) {
    return (
      <FormControlLabel
        control={radioElement}
        label={label}
        disabled={disabled}
        sx={{
          marginInlineStart: 0,
          marginInlineEnd: 0,
          "& .MuiFormControlLabel-label": {
            fontSize: "0.875rem",
            color: disabled ? "var(--text-light)" : "inherit",
          },
        }}
      />
    );
  }

  return radioElement;
});
RadioButton.displayName = "RadioButton";
export default RadioButton;
